// =============================================================================
// src/features/explore/explore-live-hydrate.ts — Orquestra a hidratacao ao vivo.
//
// SRP: "fetch -> overlay -> apply" com fallback, MEMOIZADO (roda uma vez por sessao).
//   Cliente null (integracao desligada) ou falha de rede -> mantem o baseline
//   estatico (graceful degradation, lista defensiva do CLAUDE.md) — NUNCA rejeita,
//   para nao quebrar a abertura do mapa. A origem efetiva alimenta a proveniencia.
// =============================================================================

import type { AtalaiaClient } from '../../app/types';
import { applyExploreLiveOverlay } from '../../data';
import { buildExploreOverlay } from '../../data/atalaia-overlay';

/** Origem efetiva dos dados da Explorar apos a hidratacao. */
export type ExploreLiveSource = 'live' | 'static';

/** Status resolvido da hidratacao ao vivo. */
export interface ExploreLiveStatus {
  readonly source: ExploreLiveSource;
}

/** Promise memoizada da hidratacao (1x por sessao). */
let hydration: Promise<ExploreLiveStatus> | null = null;
/** Origem resolvida: 'static' ate a hidratacao concluir como 'live'. */
let resolvedSource: ExploreLiveSource = 'static';

/**
 * Hidrata o dataset com o overlay ao vivo do Hasura (memoizado). O mapa aguarda esta
 * promise ANTES de calcular quartis/pintar, garantindo que cores/cards/legenda leiam
 * o dado ja mesclado (sem corrida com o fetch dos GeoJSON).
 */
export function hydrateExploreLive(client: AtalaiaClient | null): Promise<ExploreLiveStatus> {
  if (!hydration) hydration = runHydration(client);
  return hydration;
}

/** Origem resolvida dos dados ('static' por padrao; vira 'live' ao hidratar). */
export function exploreLiveSource(): ExploreLiveSource {
  return resolvedSource;
}

/** Busca ao vivo -> overlay -> aplica; qualquer falha cai no baseline estatico. */
async function runHydration(client: AtalaiaClient | null): Promise<ExploreLiveStatus> {
  if (!client) return { source: 'static' };
  try {
    const rows = await client.fetchLiveMunis();
    applyExploreLiveOverlay(buildExploreOverlay(rows));
    resolvedSource = 'live';
    return { source: 'live' };
  } catch {
    return { source: 'static' };
  }
}

/** TEST-ONLY: zera a memoizacao + a origem (independencia entre testes F.I.R.S.T). */
export function __resetExploreLiveForTests(): void {
  hydration = null;
  resolvedSource = 'static';
}
